import { useCallback, useEffect, useState } from "react";
import type { IWasteMovementDwtCorrection } from "@/customTypings/wasteMovement/WasteMovement";
import { wasteMovementsService } from "@/services/wasteMovements/wasteMovements.service";
import { logErrorForDev } from "@/utils";

export type WasteMovementDwtCorrectionState =
    | { status: "loading" }
    | { status: "error" }
    | { status: "loaded"; correction: IWasteMovementDwtCorrection };

/**
 * S2D: what correcting the movement's accepted DEFRA filing would involve now, as the server reports it - read only while
 * something of the movement is filed and the user may change movements. `reload` reads it again (after a save, a
 * correction or a refresh); `show` displays one the server returned.
 */
export function useWasteMovementDwtCorrection(
    siteId: string | undefined,
    movementId: string | undefined,
    version: string | undefined,
    enabled: boolean
): {
    state: WasteMovementDwtCorrectionState | null;
    reload: () => void;
    show: (correction: IWasteMovementDwtCorrection) => void;
} {
    const [state, setState] = useState<WasteMovementDwtCorrectionState | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        if (!enabled || !siteId || !movementId) {
            setState(null);
            return;
        }
        let cancelled = false;
        setState((current) => (current?.status === "loaded" ? current : { status: "loading" }));
        wasteMovementsService
            .getDwtCorrection(siteId, movementId)
            .then((correction) => {
                if (!cancelled) setState({ status: "loaded", correction });
            })
            .catch((e: unknown) => {
                logErrorForDev("wasteMovement.dwtCorrection", e);
                if (!cancelled) setState({ status: "error" });
            });
        return () => {
            cancelled = true;
        };
    }, [siteId, movementId, version, enabled, reloadToken]);

    const reload = useCallback(() => setReloadToken((n) => n + 1), []);
    const show = useCallback((correction: IWasteMovementDwtCorrection) => setState({ status: "loaded", correction }), []);

    return { state, reload, show };
}
